// Where a card payment may be sent, and which wallets can send it.
//
// Pure data handling, kept out of cards.ts so it can be tested: that module
// reaches the API client, which reads Vite's import.meta.env and cannot be
// loaded outside a bundler.

import type { Chain } from '@incpt/shared';

/** Card payment destinations, keyed by the chain they are valid on. A chain
 *  the server did not name cannot be paid from — see cardPaySources(). */
export type CardTreasury = Partial<Record<Chain, string | null>>;

/**
 * Pay-from sources filtered to the chains this treasury actually covers.
 *
 * The destination used to be picked with `chain === 'eth' ? eth : trx`, so a
 * source on any other chain was aimed at the TRON address. The transfer route
 * rejects a recipient that does not match its chain, so nothing was lost — but
 * the user was shown a wallet they could not pay from, and the error said
 * "invalid recipient", which looks like their address was wrong.
 */
export function cardPaySources<T extends { chain: Chain }>(
  sources: readonly T[],
  treasury: CardTreasury,
): T[] {
  return sources.filter((s) => !!treasury[s.chain]);
}

/** Where a card payment on this source's chain must go, if anywhere. */
export function cardPayTo(chain: Chain, treasury: CardTreasury): string | undefined {
  return treasury[chain] ?? undefined;
}
